import React, { Fragment, useMemo, useEffect } from 'react';

import classnames from 'classnames';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import { TertiaryButton } from 'components/uiLibrary/LinkButton';
import Loader from 'components/uiLibrary/Loader';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

// eslint-disable-next-line import/no-cycle
import { COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';

import { BOOLEAN_SEARCH_PARTS, TP, URL_SLUG_TYPES } from 'constants/index';

import { getBooleanSearchQuery, serializeRoleOverlapExpression } from 'utils/casting';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation, Trans } from 'src/i18n';

import { CASTING_RESULT_TYPES_VALUES, OPERATORS } from 'operabase-router/constants';

import classes from './RoleOverlap.module.scss';
import RoleOverlapBtn from './RoleOverlapBtn';

const ViewAllBtn = ({ className, vertical, orange, isCastingProfile, trackingData = {} }) => {
  const { showQuickView } = useQuickView();
  const { t } = useTranslation('NS_APP_GLOBALS');

  const handleViewAll = () => {
    showQuickView({
      type: QUICK_VIEW_TYPES.ROLEOVERLAP,
      data: {
        title: t(`${TP}.ROLES_WORKS_OVERLAP`),
      },
    });
  };

  return (
    <div className={classes.roleOverlap__viewAll}>
      <TertiaryButton
        styles={{
          root: classnames(classes.viewAllBtn, {
            [className]: !!className,
            [classes.viewAllBtn_vertical]: vertical,
            [classes.viewAllBtn_orange]: orange,
          }),
        }}
        onClick={handleViewAll}
        rightIcon={<SpriteIcon icon="chevron_right" size={16} />}
        trackingData={{
          ...trackingData,
          component: COMPONENTS.ROLE_OVERLAP,
          subComponent: SUB_COMPONENTS.VIEW_ALL_CTA,
        }}
      >
        <div className={classes.text}>
          <Typography size={12} weight="medium" className={classes.viewText}>
            <SpriteIcon icon="union" size={12} />
            {isCastingProfile ? t(`${TP}.VIEW_ALL_RELATED`) : t(`${TP}.VIEW_ALL`)}
          </Typography>
          <Typography size={11}>
            {isCastingProfile
              ? `${t(`${TP}.FILTER_PROFESSIONS`)} / ${t(`${TP}.m_ROLES`)} / ${t(`${TP}.m_WORKS`)}`
              : t(`${TP}.ROLES_WORKS_OVERLAP`)}
          </Typography>
        </div>
      </TertiaryButton>
    </div>
  );
};

const RoleOverlap = ({
  className,
  limit = 2,
  vertical = false,
  isCastingProfile = false,
  isCastingTool = false,
  orange = false,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { isDesktop } = useDeviceTypeLayouts();
  const { linkProps, h1Title } = obRouteContext;
  const { filters, tab } = linkProps;
  const { entity } = obRouteContext?.page || {};
  const roleName = (h1Title || [])?.flat()?.[0]?.name;
  const isRoleDebut = tab === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT;
  const isDesiredRole = tab === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE;
  const booleanQuery = useMemo(() => getBooleanSearchQuery(filters?.expression), [filters?.expression]);
  const isOrOperator = booleanQuery?.some(
    item => item?.type === BOOLEAN_SEARCH_PARTS.OPERATOR && item?.value === OPERATORS.SEPARATOR,
  );
  const expression = useMemo(() => {
    const formattedBooleanQuery =
      (isRoleDebut || isDesiredRole || isOrOperator) && booleanQuery?.length > 0 ? [booleanQuery?.[0]] : booleanQuery;
    const transformedIds = serializeRoleOverlapExpression(formattedBooleanQuery);
    return transformedIds;
  }, [isRoleDebut, isDesiredRole, isOrOperator, booleanQuery]);

  const { data: rolesOverlapData, isLoading: rolesOverlapDataLoading, refetch: refetchRolesOverlap } = useQuery(
    queries.getWorkRoleOverlap({
      filters: {
        ...(expression && { expression }),
        ...(entity?.id && { profileId: entity.id }),
      },
      limit,
      queryConfig: {
        enabled: !!booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id || !!entity?.id,
        select: data => {
          const workIds = booleanQuery
            ?.filter(item => item?.type === BOOLEAN_SEARCH_PARTS.QUERY)
            ?.map(item => item?.value?.[URL_SLUG_TYPES.WORK]);
          return data?.data?.filter(item => !workIds?.includes(item?.work?.id));
        },
      },
    }),
  );

  useEffect(() => {
    refetchRolesOverlap();
  }, [isRoleDebut, isDesiredRole, refetchRolesOverlap]);

  if (!expression && !entity?.id) {
    return null;
  }

  if (!rolesOverlapData?.length) {
    return null;
  }

  if (rolesOverlapDataLoading) {
    return <Loader small />;
  }

  const Wrapper = isDesktop ? HorizontalScroller : Fragment;

  return (
    <div>
      <div
        className={classnames(classes.roleOverlap, {
          [classes.roleOverlap_vertical]: vertical,
          [classes.roleOverlap_castingProfile]: isCastingProfile,
        })}
      >
        <Wrapper>
          <div
            className={classnames(classes.roleOverlap__roles, {
              [classes.roleOverlap__roles_vertical]: vertical,
              [classes.roleOverlap__roles_castingProfile]: isCastingProfile,
            })}
          >
            {isCastingProfile && (
              <Typography size="12" weight="bold" className={classes.roleOverlap__roles_title}>
                {t(`${TP}.CAST_FOR`)}
              </Typography>
            )}
            {isCastingTool && !isDesktop && (
              <ViewAllBtn
                isCastingProfile={isCastingProfile}
                className={className}
                vertical={vertical}
                orange={orange}
                trackingData={trackingData}
              />
            )}
            {rolesOverlapData?.slice(0, limit).map(role => (
              <RoleOverlapBtn
                role={role}
                className={className}
                key={role.id}
                vertical={vertical}
                orange={orange}
                trackingData={trackingData}
              />
            ))}
          </div>
        </Wrapper>
        {!isCastingTool && (
          <ViewAllBtn
            isCastingProfile={isCastingProfile}
            className={className}
            vertical={vertical}
            orange={orange}
            trackingData={trackingData}
          />
        )}
      </div>
      {isOrOperator && (
        <Typography size="12" className={classes.roleOverlap__roleInfo}>
          <Trans
            i18nKey={`${TP}.ROLE_OVERLAP_INFO`}
            ns="NS_APP_GLOBALS"
            components={{ b: <b /> }}
            values={{ roleName }}
          />
        </Typography>
      )}
    </div>
  );
};

export default RoleOverlap;
